import { useMemo } from 'react';
import { tw } from '@/lib/tailwind';
import { Info, UserPlus, X } from '@alchemer/ui/icons';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@alchemer/ui/components/select';
import { Switch } from '@alchemer/ui/components/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@alchemer/ui/components/tooltip';
import { canBeEditor, SHARE_PERMISSION } from '@/types';
import type { ShareAccessLevel, ShareListItemData } from '@/types';

const AVATAR_COLORS = [
  'bg-amber-600',
  'bg-teal-600',
  'bg-indigo-600',
  'bg-rose-600',
  'bg-sky-600',
  'bg-violet-600',
  'bg-emerald-600',
  'bg-orange-600',
  'bg-cyan-600',
  'bg-pink-600',
];

function getInitials(name: string): string {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return initials || '?';
}

function getAvatarColor(id: string): string {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

interface ShareListItemProps {
  item: ShareListItemData;
  variant: 'shared' | 'available';
  onAdd?: (item: ShareListItemData) => void;
  onRemove?: (item: ShareListItemData) => void;
  onPermissionChange?: (id: string, permission: ShareAccessLevel) => void;
  onShareDataChange?: (id: string, shareData: boolean) => void;
}

function ShareListItem({ item, variant, onAdd, onRemove, onPermissionChange, onShareDataChange }: ShareListItemProps) {
  const initials = useMemo(() => getInitials(item.name), [item.name]);
  const avatarColor = useMemo(() => getAvatarColor(item.id), [item.id]);

  // Guard against a mismatch where a user is stored as editor but lacks the required privilege.
  const effectivePermission =
    !canBeEditor(item.userRole) && item.sharedAsPermission === SHARE_PERMISSION.EDITOR
      ? SHARE_PERMISSION.VIEWER
      : item.sharedAsPermission;

  return (
    <li className={tw('flex items-center justify-between gap-4 border-b', 'border-border px-3 py-2 last:border-b-0')}>
      <div
        aria-hidden="true"
        className={tw('flex size-12 shrink-0 items-center justify-center', 'rounded-full text-white', avatarColor)}
      >
        {initials}
      </div>
      <div className={tw('min-w-0 flex-1')}>
        <div>
          <span className="sr-only">Name: </span>
          {item.name}
        </div>
        <div className={tw('text-sm text-gray-500')}>
          <span className="sr-only">Email: </span>
          {item.email}
        </div>
      </div>

      {variant === 'shared' && (
        <>
          <div className={tw('flex items-center gap-1 text-sm text-gray-500')}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="About share underlying data"
                  className={tw(
                    'focus-visible:ring-billiard-700 flex items-center focus-visible:ring-2 focus-visible:outline-none',
                    'rounded focus-visible:ring-offset-1',
                  )}
                >
                  <Info className={tw('h-4 w-4 text-gray-500')} />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className={tw(
                  'bg-background-900 z-99999 max-w-48 px-[0.8rem] py-[0.4rem]',
                  'text-[0.75rem] text-white',
                )}
              >
                When enabled, shared users can drill into the raw data behind the dashboard&apos;s charts and metrics.
                Leave off to share summary views only.
              </TooltipContent>
            </Tooltip>
            <label
              htmlFor={`shareDataSwitch-${item.id}`}
              className={tw('cursor-pointer')}
            >
              Share underlying data
            </label>
            <Switch
              id={`shareDataSwitch-${item.id}`}
              checked={item.shareUnderlyingData}
              onCheckedChange={(v) => onShareDataChange?.(item.id, v)}
              className="data-[state=unchecked]:bg-gray-600"
            />
          </div>
          <div>
            <Select
              value={effectivePermission}
              onValueChange={(v) => onPermissionChange?.(item.id, v as ShareAccessLevel)}
            >
              <SelectTrigger
                size="sm"
                className="w-24 border-gray-400"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-99999 bg-white">
                {canBeEditor(item.userRole) && <SelectItem value={SHARE_PERMISSION.EDITOR}>Editor</SelectItem>}
                <SelectItem value={SHARE_PERMISSION.VIEWER}>Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      <div className={tw('w-6')}>
        {variant === 'shared' ? (
          <button
            type="button"
            aria-label={`Remove ${item.name}`}
            className={tw(
              'focus-visible:ring-billiard-700 rounded focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
            )}
            onClick={() => onRemove?.(item)}
          >
            <X className={tw('size-5 cursor-pointer text-gray-500 hover:text-red-500')} />
          </button>
        ) : (
          <button
            type="button"
            aria-label={`Add ${item.name}`}
            className={tw(
              'focus-visible:ring-billiard-700 rounded focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
            )}
            onClick={() => onAdd?.(item)}
          >
            <UserPlus className={tw('hover:text-primary-700 size-5 cursor-pointer text-gray-500')} />
          </button>
        )}
      </div>
    </li>
  );
}

export { ShareListItem };
